import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, WifiOff } from 'lucide-react';
import { offlineStateService } from '../offline/services/offline-state.service.js';

const DISPLAY_DELAY = 5000;

function WeakConnectionWarning() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const timerRef = useRef(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const unsubscribe = offlineStateService.subscribe((state) => {
      if (state.isDegraded && !dismissed) {
        if (!timerRef.current) {
          timerRef.current = setTimeout(() => {
            setVisible(true);
          }, DISPLAY_DELAY);
        }
      } else if (!state.isDegraded) {
        clearTimer();
        setVisible(false);
        setDismissed(false);
      }
    });

    return () => {
      unsubscribe();
      clearTimer();
    };
  }, [dismissed, clearTimer]);

  const handleDismiss = () => {
    setVisible(false);
    setDismissed(true);
    clearTimer();
  };

  if (!visible) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      <div className="relative bg-[#131b2b] rounded-3xl border border-amber-500/30 shadow-2xl w-full max-w-md overflow-hidden">
        <div className="flex items-center gap-3 p-6 border-b border-amber-500/20 bg-amber-500/10">
          <div className="p-2 bg-amber-500/20 rounded-xl">
            <WifiOff size={24} className="text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">
              {t("errors.weakConnectionTitle")}
            </h2>
          </div>
        </div>

        <div className="p-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-amber-500/10 mb-4">
              <AlertCircle size={40} className="text-amber-400" />
            </div>
            <p className="text-gray-300">
              {t("errors.weakConnection")}
            </p>
          </div>

          <div className="bg-amber-500/5 rounded-xl p-4 border border-amber-500/10">
            <p className="text-sm text-gray-400 whitespace-pre-line">
              {t("errors.weakConnectionSolutions")}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center gap-3 p-6 border-t border-amber-500/20">
          <button
            onClick={handleDismiss}
            className="px-6 py-2.5 rounded-xl text-sm font-medium bg-amber-500 hover:bg-amber-400 text-white transition active:scale-95"
          >
            {t("common.gotIt")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default WeakConnectionWarning;
